import type { MentionPerson } from '@till-erp/platform/mentions';
/** Gemeinsame flüchtige Entwürfe für Vollchat und Messenger; niemals Browser-Speicher. */
import type { ChatReference, ChatFileView } from '@till-erp/chat/values';
import { createChatMemory, invalidateChatMemoryLease, onChatMemoryClear } from './chat-memory.ts';
export interface ChatDraftFile {
  id: string;
  file: File;
  preview: string | null;
  state: 'selected' | 'uploading' | 'ready' | 'failed' | 'cancelled' | 'removing' | 'remove-failed';
  progress: number;
  error: string | null;
  uploaded: ChatFileView | null;
  cancel?: (() => void) | undefined;
  /** Upload abgeschickt: ohne bestätigte Antwort kann der Server die Datei trotzdem gespeichert haben. */
  attempted?: boolean | undefined;
}
export interface ChatDraft { text: string; files: ChatDraftFile[]; references?: ChatReference[]; mentions?: MentionPerson[]; sendKey?: string }
const empty: ChatDraft = { text: '', files: [] };
export interface ChatPendingMessage { key: string; body: string; files: ChatFileView[]; references?: ChatReference[]; mentions?: MentionPerson[]; createdAt: string; state: 'sending' | 'failed'; error: string | null }
export const chatPendingByChannel = createChatMemory<ChatPendingMessage[]>();
const sending = new Set<string>();
export function claimChatDraftSend(key: string) { if (sending.has(key)) return false; sending.add(key); return true; }
export function releaseChatDraftSend(key: string) { sending.delete(key); }
const drafts = new Map<string, ChatDraft>();
const listeners = new Set<() => void>();
const emptyPending: ChatPendingMessage[] = [];
export const readChatPending = (key: string) => chatPendingByChannel.get(key) ?? emptyPending;
export function updateChatPending(key: string, update: (list: ChatPendingMessage[]) => ChatPendingMessage[]) {
  const next = update(readChatPending(key));
  if (next.length) chatPendingByChannel.set(key, next); else chatPendingByChannel.delete(key);
  for (const listener of listeners) listener();
}
export function retainChatConversationDrafts(userId: string, channelIds: ReadonlySet<unknown>) {
  for (const key of new Set([...drafts.keys(), ...chatPendingByChannel.keys()])) {
    if (!key.startsWith(`${userId}:`)) continue;
    const channelId = key.slice(userId.length + 1).split(':')[0];
    if (channelId && !channelIds.has(channelId)) clearChatConversationDraft(userId, channelId);
  }
}
export const chatDraftKey = (userId: string, channelId: string, draftScope?: string) => `${userId}:${channelId}${draftScope ? `:${draftScope}` : ''}`;
export const readChatDraft = (key: string) => drafts.get(key) ?? empty;
/** A closed host must protect drafts in every thread, including reference-only drafts from24.8. */
export function hasChatConversationDraft(userId: string, channelId: string): boolean {
  const base = chatDraftKey(userId, channelId);
  const belongs = (key: string) => key === base || key.startsWith(`${base}:`);
  for (const [key, draft] of drafts) {
    if (!belongs(key)) continue;
    const references = 'references' in draft && Array.isArray(draft.references) ? draft.references.length : 0;
    if (draft.text || draft.files.length || references) return true;
  }
  return [...chatPendingByChannel].some(([key, pending]) => belongs(key) && pending.length > 0);
}
export function subscribeChatDraft(listener: () => void) { listeners.add(listener); return () => { listeners.delete(listener); }; }
export function updateChatDraft(key: string, update: (draft: ChatDraft) => ChatDraft) {
  drafts.set(key, update(readChatDraft(key)));
  for (const listener of listeners) listener();
}
export function clearChatDraft(key: string) {
  for (const file of drafts.get(key)?.files ?? []) { file.cancel?.(); if (file.preview) URL.revokeObjectURL(file.preview); }
  drafts.delete(key);
  sending.delete(key);
  for (const listener of listeners) listener();
}
/**
 * Nach erfolgreichem Senden nur den tatsächlich gesendeten Stand entfernen: Ein während der Übertragung ergänzter Anhang
 * bleibt als neuer Entwurf stehen (mit neuem Schlüssel). Text und Bezüge ändern den Schlüssel sofort, sind also bei
 * gleichem Schlüssel genau die gesendeten.
 */
export function clearSentChatDraft(key: string, sendKey: string, sentFileIds: readonly string[]) {
  const draft = readChatDraft(key);
  if (draft.sendKey !== sendKey) return;
  const sent = new Set(sentFileIds);
  const rest = draft.files.filter((file) => !sent.has(file.id));
  if (!rest.length) { clearChatDraft(key); return; }
  for (const file of draft.files) if (sent.has(file.id) && file.preview) URL.revokeObjectURL(file.preview);
  updateChatDraft(key, (d) => ({ ...d, text: '', references: [], sendKey: undefined, files: rest }));
}
export function clearChatDrafts(userId?: string) {
  for (const key of new Set([...drafts.keys(), ...chatPendingByChannel.keys(), ...sending])) {
    if (userId && !key.startsWith(`${userId}:`)) continue;
    invalidateChatMemoryLease(key);
    clearChatDraft(key);
    chatPendingByChannel.delete(key);
  }
  for (const listener of listeners) listener();
}
export function clearChatConversationDraft(userId: string, channelId: string) {
  const base = chatDraftKey(userId, channelId);
  for (const key of new Set([...drafts.keys(), ...chatPendingByChannel.keys()])) {
    if (key === base || key.startsWith(`${base}:`)) { invalidateChatMemoryLease(key); clearChatDraft(key); updateChatPending(key, () => []); }
  }
}
// Bestätigter Rechte- oder Mitgliedschaftsentzug (24.2) löscht auch Text, Anhänge und wartende Nachrichten des Composers
// (Hauptnachricht und Threads, 24.5); ohne erlaubte Gespräche alles dieser Person.
onChatMemoryClear((userId, allowedChannels) => {
  if (allowedChannels) retainChatConversationDrafts(userId, allowedChannels);
  else clearChatDrafts(userId);
});
